import { useEffect, useState } from "react";
import { Loader2, UserCheck, UserX, GraduationCap, ArrowRightLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getMinisterHistory } from "@/data/ministerRepository";
import type { MinisterHistoryEvent } from "../../types";

interface MinisterHistoryTimelineProps {
  ministerId: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-BO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function EventIcon({ type, isInactivation }: { type: MinisterHistoryEvent["event_type"]; isInactivation?: boolean }) {
  if (type === "status_change") {
    return isInactivation ? <UserX className="size-4" /> : <UserCheck className="size-4" />;
  }
  if (type === "degree_update") return <GraduationCap className="size-4" />;
  return <ArrowRightLeft className="size-4" />;
}

function eventIconClasses(type: MinisterHistoryEvent["event_type"], isInactivation?: boolean): string {
  if (type === "status_change") {
    return isInactivation
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : "border-green-500/40 bg-green-500/10 text-green-600";
  }
  if (type === "degree_update") return "border-blue-500/40 bg-blue-500/10 text-blue-600";
  return "border-amber-500/40 bg-amber-500/10 text-amber-600";
}

function EventBody({ event }: { event: MinisterHistoryEvent }) {
  if (event.event_type === "status_change") {
    const isInactivation = event.status_to === "inactive";
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={isInactivation ? "destructive" : "default"}
          className={isInactivation ? "" : "bg-green-500/15 text-green-700 hover:bg-green-500/20"}
        >
          {isInactivation ? "Inactivo" : "Activo"}
        </Badge>
        {event.inactive_reason && (
          <span className="text-sm text-muted-foreground">· {event.inactive_reason.label}</span>
        )}
      </div>
    );
  }

  if (event.event_type === "degree_update") {
    const from = event.degree_from_relation?.name ?? "—";
    const to = event.degree_to_relation?.name ?? "—";
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-blue-500/40 text-blue-600">Grado actualizado</Badge>
        <span className="text-sm text-muted-foreground">
          {from} → {to}
        </span>
      </div>
    );
  }

  // transfer
  const districtFrom = event.district_from_relation?.name;
  const districtTo = event.district_to_relation?.name;
  const regionFrom = event.region_from_relation?.name;
  const regionTo = event.region_to_relation?.name;

  const parts: string[] = [];
  if (districtFrom || districtTo) parts.push(`Distrito: ${districtFrom ?? "—"} → ${districtTo ?? "—"}`);
  if (regionFrom || regionTo) parts.push(`Región: ${regionFrom ?? "—"} → ${regionTo ?? "—"}`);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="border-amber-500/40 text-amber-600">Transferencia</Badge>
      <span className="text-sm text-muted-foreground">{parts.join(" · ")}</span>
    </div>
  );
}

export function MinisterHistoryTimeline({ ministerId }: MinisterHistoryTimelineProps) {
  const [events, setEvents] = useState<MinisterHistoryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getMinisterHistory(ministerId).then((data) => {
      if (!cancelled) {
        setEvents(data);
        setIsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [ministerId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Sin historial registrado.
      </p>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, index) => {
        const isInactivation = event.event_type === "status_change" && event.status_to === "inactive";
        const isLast = index === events.length - 1;

        return (
          <div key={event.id} className="flex gap-4">
            {/* Spine */}
            <div className="flex flex-col items-center">
              <div className={`flex size-8 shrink-0 items-center justify-center rounded-full border-2 ${eventIconClasses(event.event_type, isInactivation)}`}>
                <EventIcon type={event.event_type} isInactivation={isInactivation} />
              </div>
              {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1 pb-6">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <EventBody event={event} />
                <span className="text-xs text-muted-foreground">{formatDate(event.event_date)}</span>
              </div>
              {event.created_by_name && (
                <p className="mt-0.5 text-xs text-muted-foreground">Por: {event.created_by_name}</p>
              )}
              {event.notes && (
                <p className="mt-1 text-sm text-muted-foreground">{event.notes}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
